import React from 'react';
import { DoorClosedLocked, MonitorCloud, Target, Rocket, Fingerprint }  from 'lucide-react';
import Footer from '../../components/HeaderFooter/Footer';
import { useTheme } from '../../Context/themeContext';

const About = () => {

  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const bgPrimary = isDark ? 'bg-gradient-to-br from-richblue-950 via-richblue-900 to-richblue-950' : 'bg-gradient-to-br from-gray-50 via-blue-100/20 to-gray-50';
  const bgCard = isDark ? 'bg-richblue-800/30' : 'bg-white/80';
  const borderColor = isDark ? 'border-blue-800/40' : 'border-gray-200';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const serviceBg = isDark ? 'bg-richblue-900/50 hover:bg-richblue-800/80' : 'bg-gray-100/50 hover:bg-gray-200/80';
  const bgGlass = isDark ? 'bg-white/5' : 'bg-gray-100/80';

  const getColorClasses = (color, type = 'bg') => {
    const colors = {
      blue: {
        bg: isDark ? 'bg-blue-600' : 'bg-blue-500',
        bgLight: isDark ? 'bg-blue-600/20' : 'bg-blue-100/30',
        text: isDark ? 'text-blue-400' : 'text-blue-600',
        border: isDark ? 'border-blue-600/30' : 'border-blue-300',
        gradient: isDark ? 'from-blue-600 to-blue-800' : 'from-blue-500 to-blue-700'
      },
      purple: {
        bg: isDark ? 'bg-purple-600' : 'bg-purple-500',
        bgLight: isDark ? 'bg-purple-600/20' : 'bg-purple-100/30',
        text: isDark ? 'text-purple-400' : 'text-purple-600',
        border: isDark ? 'border-purple-600/30' : 'border-purple-300',
        gradient: isDark ? 'from-purple-600 to-purple-800' : 'from-purple-500 to-purple-700'
      },
      indigo: {
        bg: isDark ? 'bg-yellow-600' : 'bg-yellow-500',
        bgLight: isDark ? 'bg-yellow-600/20' : 'bg-yellow-100/10',
        text: isDark ? 'text-yellow-400' : 'text-yellow-600',
        border: isDark ? 'border-yellow-600/30' : 'border-yellow-300',
        gradient: isDark ? 'from-yellow-600 to-yellow-800' : 'from-yellow-500 to-yellow-700'
      },
      cyan: {
        bg: isDark ? 'bg-cyan-600' : 'bg-cyan-500',
        bgLight: isDark ? 'bg-cyan-600/20' : 'bg-cyan-100/30',
        text: isDark ? 'text-cyan-400' : 'text-cyan-600',
        border: isDark ? 'border-cyan-600/30' : 'border-cyan-300',
        gradient: isDark ? 'from-cyan-600 to-cyan-800' : 'from-cyan-500 to-cyan-700'
      }
    };
    return colors[color] || colors.blue;
  };

 const Mobile = (props) => (
    <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
          >
    <path d="M8 18h.01" />
    <rect x="3" y="8" width="10" height="14" rx="2" />
    </svg>
  );

  const services = [
    {
      icon: Fingerprint,
      title: 'Biometric Hardware',
      description: 'Advanced Biometric Attendance Device with Cloud data Syncronisation.',
      features: ['Fingerprint Scanner', 'Time Syncronisation with internet and Offline Time Retention', 'Built In Buzzer', 'LED Status Indicator', 'Work with Mobile OTG for Power', 'Connect via Wifi'],
      color: 'purple'
    },
    {
      icon: DoorClosedLocked,
      title: 'Access Control Hardware',
      description: 'Door Access control Mechanism for access control',
      features: ['One and Two way Relay Control', '12 volt Adapter', 'Exit Switch', 'Fire Switch', 'Electromagnetic Lock'],
      color: 'blue'
    },
    {
      icon: MonitorCloud,
      title: 'Attendance Software',
      description: 'Cloud based Software with real-time tracking and automated attendance count.',
      features: ['Real-Time Tracking', 'Wallet System', 'Auto-deductions', 'Payroll Integration', 'Export Attendance'],
      color: 'indigo'
    },
    {
      icon: Mobile,
      title: 'Mobile Application',
      description: 'Cloud based Application with real-time tracking and notifications.',
      features: ['Android', 'IOS', 'Real Time Notifications', 'Leave Approval'],
      color: 'cyan'
    }
  ];

  // Company Description Component
  const CompanyDescription = () => (
    <div className="space-y-12">
      {/* Hero Description */}
       {/* ABOUT SECTION */}
                        <div className="relative group backdrop-blur-sm">
                            <div className={`absolute inset-0 transition-opacity duration-500  rounded-3xl blur-2xl opacity-20 group-hover:opacity-30`}></div>

                            <div className={`relative p-10 border ${bgCard} rounded-3xl ${borderColor} backdrop-blur-xl`}>
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500">
                                        <Fingerprint className="w-8 h-8 text-white" />
                                    </div>
                                    <div>
                                        <h2 className={`text-3xl font-bold md:text-4xl ${textPrimary}`}>
                                            About CICO Time
                                        </h2>
                                        <div className="w-20 h-1 mt-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"></div>
                                    </div>
                                </div>

                                <p className={`text-lg leading-relaxed ${isDark ? 'text-blue-200/70' : 'text-gray-700'}`}>
                                    Designed in 2024, <span className="font-semibold text-transparent bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text">CICO-Time</span> was built to eliminate
                                    manual sheets and proxy. We combine cutting-edge algorithm with intuitive design to make
                                    management work effortless.
                                </p>

                                <p className={`mt-6 text-lg leading-relaxed ${isDark ? 'text-blue-200/70' : 'text-gray-700'}`}>
                                    Our platform processes over <span className={`px-2 py-1 font-semibold rounded-lg ${isDark ? 'text-white bg-white/10' : 'text-gray-900 bg-gray-200'}`}>20,000+ Check-ins monthly</span> across 20+ Organizations.
                                    From Small to Medium size Organizations, we provide the flexibility to track via Cloud based Mobile Application and Website.
                                </p>

                                <div className="flex flex-wrap gap-4 mt-8">
                                    {[
                                        { icon: "fa-globe", text: "1+ Countries" },
                                        { icon: "fa-building", text: "20+ Organizations" },
                                        { icon: "fa-clock", text: "Since 2024" }
                                    ].map((item, index) => (
                                         <div key={index} className={`flex items-center gap-2 px-4 py-2 border rounded-full ${bgGlass} ${borderColor}`}>
                                            <i className={`fas ${item.icon} ${isDark ? 'text-blue-200/70' : 'text-gray-700'}`}></i>
                                            <span className={`text-sm ${isDark ? 'text-blue-100' : 'text-gray-700'}`}>{item.text}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
      {/* Company Overview */}
      <div className="grid gap-8 md:grid-cols-2">
        <div className={`p-8 border ${bgCard} backdrop-blur-sm ${borderColor} rounded-2xl transition-colors duration-300`}>
          <Target className={`w-10 h-10 mb-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
          <h3 className={`mb-4 text-2xl font-bold ${textPrimary}`}>Our Mission</h3>
          <p className={`leading-relaxed ${isDark ? 'text-blue-200/70' : 'text-gray-700'}`}>
            To transform workforce management by providing innovative, accessible, and affordable 
            biometric solutions with a fair, usage-based pricing model that ensures businesses 
            only pay for what they actually use.
          </p>
        </div>
        
        <div className={`p-8 border ${bgCard} backdrop-blur-sm ${borderColor} rounded-2xl transition-colors duration-300`}>
          <Rocket className={`w-10 h-10 mb-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
          <h3 className={`mb-4 text-2xl font-bold ${textPrimary}`}>Our Vision</h3>
          <p className={`leading-relaxed ${isDark ? 'text-blue-200/70' : 'text-gray-700'}`}>
            To become the global standard for intelligent workforce management, where every 
            organization can access enterprise-grade attendance solutions with transparent, 
            pay-per-use pricing.
          </p>
        </div>
      </div>

      {/* What We Do */}
      <div className={`p-8 border ${bgCard} backdrop-blur-sm ${borderColor} rounded-3xl transition-colors duration-300`}>
        <h2 className={`mb-8 text-3xl font-bold text-center ${textPrimary}`}>What We Do</h2>
        
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {services.map((service, index) => {
            const Icon = service.icon;
            const colors = getColorClasses(service.color);
            return (
              <div key={index} className={`relative p-6 transition-all duration-300 ${serviceBg} rounded-xl`}>
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${colors.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className={`mb-2 text-lg font-semibold ${textPrimary}`}>{service.title}</h3>
                <p className={`text-sm ${isDark ? 'text-blue-200/60' : 'text-gray-600'}`}>{service.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (<div className={`${bgPrimary} overflow-hidden transition-colors duration-300`}>
       <div className="px-4 py-12 mx-auto mt-10 max-w-7xl sm:px-6 lg:px-8">

       <CompanyDescription />
       </div>
      <Footer />
          </div>
          );
};

export default About;
